import React, { useEffect } from 'react'
import { useDrop } from 'react-dnd'

import { setRunsheetShiftTableLoading } from '@/entities/gridTables/slices/runsheetShiftTablesSlice'
import { setRunsheetUnassignedTableLoading } from '@/entities/gridTables/slices/runsheetUnassignedTablesSlice'
import { processAssignToRunsheet } from '@/entities/gridTables/thunks/processAssignToRunsheet'
import { processUnassignOrder } from '@/entities/gridTables/thunks/processUnassignOrders'
import { updateRunsheetShiftTableState } from '@/entities/gridTables/thunks/updateRunsheetShiftTableState'
import { updateRunsheetUnassignedTableState } from '@/entities/gridTables/thunks/updateRunsheetUnassignedTableState'
import { fetchRunsheets, ReverseStopTypeLookup, StopTypeEnum } from '@/entities/schedules'
import { StopType } from '@/entities/schedules/models/StopType'
import { GridTableDragItem, GridTableDragType } from '@/shared/components/GridTable/models/GridTableDragType'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { useAppDispatch } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { styled } from '@mui/material'
import { EntityId } from '@reduxjs/toolkit'

export type RunsheetUnassignedTableDropZoneProps = {
  runsheetId: EntityId
}

export const RunsheetUnassignedTableDropZone: React.FC<RunsheetUnassignedTableDropZoneProps> = ({ runsheetId }) => {
  const [isDraggingDebounced, setIsDraggingDebounced] = React.useState(false)
  const { setToastContent } = useToastContext()
  const dispatch = useAppDispatch()

  // Droppable zone for moving orders to inbox
  const [{ isOver, isDragging }, dropRef] = useDrop({
    accept: [GridTableDragType.SHIFT_STOP_ROW, GridTableDragType.INBOX_ORDER_ROW, GridTableDragType.UNASSIGNED_ORDER_ROW],
    drop: async (item: GridTableDragItem) => {
      if (item.type === GridTableDragType.SHIFT_STOP_ROW) {
        // Validate that the drop items are orders
        for (const stop of item.rows) {
          if (stop.stopType !== StopTypeEnum.Order) {
            setToastContent({
              open: true,
              severity: 'error',
              title: 'Failed to unassign order',
              content: `${ReverseStopTypeLookup[stop.stopType as StopType]} cannot be moved to unassigned.`,
            })

            return
          }
        }

        // Receive orders from shift
        const orderIds = item.rows.map((stop) => stop.orderId)
        const sourceRunsheetId = item.rows[0].runsheetId
        const isSameRunsheet = parseInt(sourceRunsheetId) == runsheetId
        const shiftId = item.rows[0].shiftId

        // Set Loading State
        dispatch(setRunsheetUnassignedTableLoading({ id: runsheetId, isLoading: true }))
        dispatch(setRunsheetShiftTableLoading({ id: shiftId, isLoading: true }))

        // Process Drop
        if (isSameRunsheet) {
          await dispatch(processUnassignOrder({ shiftId, orderIds }))
        } else {
          await dispatch(processAssignToRunsheet({ orderIds, sourceRunsheetId, runsheetId }))
        }

        // Fetch runsheets and update table data
        const runsheetIdsToRefresh = isSameRunsheet ? [runsheetId] : [runsheetId, sourceRunsheetId]

        await dispatch(fetchRunsheets({ runsheetIds: runsheetIdsToRefresh }))
        await Promise.allSettled([
          dispatch(updateRunsheetShiftTableState({ shiftId: shiftId })),
          dispatch(updateRunsheetUnassignedTableState({ runsheetId: runsheetId })),
        ])

        // Reset Loading State
        dispatch(setRunsheetShiftTableLoading({ id: shiftId, isLoading: false }))
        dispatch(setRunsheetUnassignedTableLoading({ id: runsheetId, isLoading: false }))
      } else if (item.type === GridTableDragType.UNASSIGNED_ORDER_ROW) {
        const orderIds = item.rows.map((row) => row.orderId)
        const sourceRunsheetId = item.rows[0].runsheetId

        // Set Loading State
        dispatch(setRunsheetUnassignedTableLoading({ id: runsheetId, isLoading: true }))
        dispatch(setRunsheetUnassignedTableLoading({ id: sourceRunsheetId, isLoading: true }))
        
        await dispatch(processAssignToRunsheet({ sourceRunsheetId, runsheetId, orderIds }))
        await dispatch(fetchRunsheets({ runsheetIds: [sourceRunsheetId, runsheetId] }))
        await Promise.allSettled([
          dispatch(updateRunsheetUnassignedTableState({ runsheetId: runsheetId })),
          dispatch(updateRunsheetUnassignedTableState({ runsheetId: sourceRunsheetId })),
        ])

        // Reset Loading State
        dispatch(setRunsheetUnassignedTableLoading({ id: runsheetId, isLoading: false }))
        dispatch(setRunsheetUnassignedTableLoading({ id: sourceRunsheetId, isLoading: false }))
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      isDragging: monitor.getItem() !== null && (
        monitor.getItemType() === GridTableDragType.SHIFT_STOP_ROW ||
        monitor.getItemType() === GridTableDragType.INBOX_ORDER_ROW
      ) || (
      // Only allow drop if the source unassigned table is not the same as the target unassigned table
        monitor.getItemType() === GridTableDragType.UNASSIGNED_ORDER_ROW &&
          monitor.getItem().rows.length > 0 &&
          monitor.getItem().rows[0].runsheetId !== runsheetId
      ),
    }),
  })

  // Debounce isDragging state to prevent dropzone from showing up when dragging within the same table
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsDraggingDebounced(isDragging)
    }, 30)

    return () => {
      clearTimeout(timeout)
    }
  }, [isDragging])

  return (
    <StyledDropZone ref={dropRef} $isOver={isOver} $isDragging={isDraggingDebounced} />
  )
}

const StyledDropZone = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isOver' && prop !== '$isDragging',
}) <{ $isOver: boolean, $isDragging: boolean }>`
  display: ${({ $isDragging }) => ($isDragging ? 'block' : 'none')};
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.palette.custom.widgetDropTarget.backgroundColor};
  border-radius: ${pxToRem(8)};
  border: ${({ theme }) => theme.palette.custom.widgetDropTarget.border};
  opacity: ${({ $isOver }) => ($isOver ? 1 : 0)};
  transition: opacity 0.1s;
  padding: ${props => props.theme.typography.pxToRem(8)};
`
